const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Escapes the five HTML-significant characters, for text built into markup. */
export function escapeHtml(text: string): string {
  return String(text ?? '').replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]!);
}

/** Escapes RegExp syntax so `text` matches literally inside `new RegExp(…)`. */
export function escapeRegExp(text: string): string {
  return String(text ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const GERMAN_FOLDS: Record<string, string> = {
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
  ß: 'ss',
  ẞ: 'ss',
};

export interface NxSlugifyOptions {
  /** Joins the words. Default `-`. */
  separator?: string;
  /** Cuts the slug at a word boundary where possible. */
  maxLength?: number;
}

/**
 * A URL/file-name friendly slug: lower case, German umlauts spelled out
 * („Größe Übersicht“ → `groesse-uebersicht`), other accents dropped, anything
 * that is not a letter or digit collapsed into one separator.
 */
export function slugify(text: string, options: NxSlugifyOptions = {}): string {
  const sep = options.separator ?? '-';
  let slug = String(text ?? '')
    .toLowerCase()
    .replace(/[äöüßẞ]/g, (c) => GERMAN_FOLDS[c]!)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join(sep);
  const max = options.maxLength;
  if (max !== undefined && max >= 0 && slug.length > max) {
    const cut = slug.slice(0, max);
    const atBoundary = !sep || slug.startsWith(sep, max);
    const boundary = sep ? cut.lastIndexOf(sep) : -1;
    slug = atBoundary || boundary <= 0 ? cut : cut.slice(0, boundary);
    while (sep && slug.endsWith(sep)) slug = slug.slice(0, -sep.length);
  }
  return slug;
}

/** First letter or digit of a word (falls back to its first character). */
function initialOf(word: string): string {
  const chars = Array.from(word);
  return chars.find((c) => /[\p{L}\p{N}]/u.test(c)) ?? chars[0] ?? '';
}

/**
 * Initials of a name, as {@link NxAvatar} derives them: the first letter of
 * the first word plus the first letter of the last word, upper-cased
 * („Anna Maria Schmidt“ → `AS`, „Anna“ → `A`). With `max > 2` the leading
 * words fill up the rest („Anna Maria Schmidt“, 3 → `AMS`). An empty name
 * gives `''`.
 *
 * Differs from the mention picker's `deriveInitials`, which returns `?` for
 * an empty name and the first two letters of a single word („Anna“ → `AN`).
 * Leading punctuation is skipped here („(Max) Mustermann“ → `MM`).
 */
export function initials(name: string, max = 2): string {
  const words = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const count = Math.max(0, Math.floor(max));
  if (words.length === 0 || count === 0) return '';
  const picked =
    words.length <= count
      ? words
      : count === 1
        ? [words[0]!]
        : [...words.slice(0, count - 1), words[words.length - 1]!];
  return picked.map(initialOf).join('').toUpperCase();
}
